// usePriceEditing.js
import React from "react";
import {
  fieldKey,
  normalizeDecimalInput,
  toNumberOrNull,
  decimalsForCurrency,
  stationHasEUR,
} from "../utils/priceFormUtils";

export function usePriceEditing({
  armexData,
  setArmexData,
  stationData,
  setStationData,
  eurAllowedFuels, // NEW
}) {
  const [draft, setDraft] = React.useState({});
  const [changed, setChanged] = React.useState({});
  const lastEditRef = React.useRef({});

  const getInputValue = (stationId, fuel, currency, dataValue) => {
    const k = fieldKey(stationId, fuel, currency);
    if (draft[k] !== undefined) return draft[k];
    return (dataValue ?? "") === null ? "" : String(dataValue ?? "");
  };

  const onBlurClearDraft = (stationId, fuel, currency) => {
    const k = fieldKey(stationId, fuel, currency);
    setDraft((prev) => {
      const next = { ...prev };
      delete next[k];
      return next;
    });
  };

  const handleChange = ({ stationId, fuelType, currency, rawValue, isArmex }) => {
    const norm = normalizeDecimalInput(rawValue, decimalsForCurrency(currency));
    const num = toNumberOrNull(norm);

    const k = fieldKey(stationId, fuelType, currency);
    setDraft((prev) => ({ ...prev, [k]: norm }));

    if (fuelType === "Kurz") lastEditRef.current[`${stationId}-Kurz`] = "KURZ";
    else lastEditRef.current[`${stationId}-${fuelType}`] = currency;

    const changedKeys = [k];

    const recalcEUR = (prices, fuel, czkValue, kurzValue, hasEUR) => {
      // NEW: EUR fuel guard
      if (!hasEUR) return prices;
      if (!eurAllowedFuels?.has?.(fuel)) return prices;

      if (
        czkValue == null || kurzValue == null ||
        !Number.isFinite(czkValue) || !Number.isFinite(kurzValue) ||
        kurzValue === 0
      ) return prices;

      const newEUR = Number((czkValue / kurzValue).toFixed(3));
      const eurKey = fieldKey(stationId, fuel, "EUR");
      changedKeys.push(eurKey);

      return {
        ...prices,
        [fuel]: {
          ...prices[fuel],
          EUR: {
            ...(prices?.[fuel]?.EUR || {}),
            value: newEUR,
            confirmed: 0,
            dirty: true,
            source: "derived",
          },
        },
      };
    };

    const setKurz = (prices, newKurz) => {
      const kurzKey = fieldKey(stationId, "Kurz", "CZK");
      changedKeys.push(kurzKey);

      return {
        ...prices,
        Kurz: {
          ...prices.Kurz,
          CZK: {
            ...(prices?.Kurz?.CZK || {}),
            value: newKurz,
            confirmed: 0,
            dirty: true,
            source: "derived",
          },
        },
      };
    };

    const updatePrices = (prevPrices) => {
      let prices = {
        ...prevPrices,
        [fuelType]: {
          ...prevPrices[fuelType],
          [currency]: {
            ...(prevPrices?.[fuelType]?.[currency] || {}),
            value: num,
            confirmed: 0,
            dirty: true,
            source: "manual",
          },
        },
      };

      const station =
        stationId === armexData?.id
          ? armexData
          : (stationData || []).find((s) => s.id === stationId);

      const hasEUR = stationHasEUR(station);
      const kurzRaw = prevPrices?.Kurz?.CZK?.value;
      const kurz = kurzRaw == null ? null : Number(kurzRaw);

      // ARMEX: EUR derived only (read-only input)
      if (isArmex) {
        if (hasEUR && fuelType !== "Kurz" && currency === "CZK" && num != null && Number.isFinite(kurz) && kurz !== 0) {
          prices = recalcEUR(prices, fuelType, num, kurz, hasEUR);
        }

        if (hasEUR && fuelType === "Kurz" && currency === "CZK") {
          const newKurz = num;
          if (newKurz != null && Number.isFinite(newKurz) && newKurz !== 0) {
            for (const [fuel, currencies] of Object.entries(prevPrices || {})) {
              if (fuel === "Kurz") continue;

              const czkVal = currencies?.CZK?.value;
              const czkNum = czkVal == null ? null : Number(czkVal);

              if (czkNum != null && Number.isFinite(czkNum) && czkNum > 0) {
                prices = recalcEUR(prices, fuel, czkNum, newKurz, hasEUR);
              }
            }
          }
        }

        return prices;
      }

      // NEARBY
      if (!hasEUR) return prices;

      if (fuelType === "Kurz" && currency === "CZK") {
        const newKurz = num;
        if (newKurz != null && Number.isFinite(newKurz) && newKurz !== 0) {
          for (const [fuel, currencies] of Object.entries(prevPrices || {})) {
            if (fuel === "Kurz") continue;

            const czkVal = currencies?.CZK?.value;
            const czkNum = czkVal == null ? null : Number(czkVal);

            if (czkNum != null && Number.isFinite(czkNum) && czkNum > 0) {
              prices = recalcEUR(prices, fuel, czkNum, newKurz, hasEUR);
            }
          }
        }
        return prices;
      }

      if (fuelType !== "Kurz") {
        const last = lastEditRef.current[`${stationId}-${fuelType}`];
        const czkNow = prices?.[fuelType]?.CZK?.value;
        const eurNow = prices?.[fuelType]?.EUR?.value;

        const czkNum = czkNow == null ? null : Number(czkNow);
        const eurNum = eurNow == null ? null : Number(eurNow);

        // edit CZK + Kurz exists => compute EUR (only if eurAllowedFuels has fuel)
        if (last === "CZK" && num != null && Number.isFinite(kurz) && kurz !== 0) {
          prices = recalcEUR(prices, fuelType, num, kurz, hasEUR);
        }

        // edit EUR + have both => compute Kurz
        // (nechávám tak jak to máš; EUR existuje jen pokud je povolený slot)
        if (last === "EUR" && czkNum != null && eurNum != null && czkNum > 0 && eurNum > 0) {
          const newKurz = Number((czkNum / eurNum).toFixed(3));
          prices = setKurz(prices, newKurz);
        }
      }

      return prices;
    };

    if (isArmex) {
      setArmexData((prev) => ({ ...prev, prices: updatePrices(prev.prices) }));
    } else {
      setStationData((prev) =>
        prev.map((s) => (s.id === stationId ? { ...s, prices: updatePrices(s.prices) } : s))
      );
    }

    setChanged((prev) => {
      const next = { ...prev };
      for (const ck of changedKeys) next[ck] = true;
      return next;
    });
  };

  return { draft, changed, getInputValue, onBlurClearDraft, handleChange };
}
